import { Bell, Copy, Ellipsis, Pin, Trash2 } from "lucide-react"

import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarShortcut,
  MenubarTrigger,
} from "@components/common/menu-bar"
import { DictType } from "@pma/types"
import DeleteProject from "./delete-project"
import { Separator } from "@components/common/separator"
import useTranslator from "@hooks/use-translator"
import CloneProject from "./clone-project"
import PinProject from "./pin-project"
import { useContext, useState } from "react"
import { ProjectContext } from "@pma/contexts/ProjectContext"
import { cn } from "@pma/utils/helpers"
import { useAuth } from "@pma/stores/auth-store"
import RestoreProject from "./restore-project"
import { ReminderSettingsModal } from "./reminder-settings-modal"

interface Props {
  project: DictType
  styled?: boolean
}
const ProjectDetailMenu = ({ project, styled = true }: Props) => {
  const { translate } = useTranslator()
  const { authConfig } = useAuth()
  const [reminderOpen, setReminderOpen] = useState(false)

  const { pinnedProjectResult } = useContext(ProjectContext)

  const pinnedByUser = pinnedProjectResult?.data?.items.some((p: DictType) => p?.id === project?.id)

  const isOrganizationOwner = authConfig?.workspace?.owner_id === authConfig?.id
  const isProjectOwner = project?.owner?.id === authConfig?.id
  const isAdmin = authConfig?.companies_of_user?.find((company: any) => {
    if (company?.id === authConfig?.last_organization_id && company?.role?.id == 22 /* ADMIN_ROLE_ID */) return true
  })

  return (
    <div className=''>
      <Menubar className='border-none bg-transparent'>
        <MenubarMenu>
          <MenubarTrigger
            className={cn(
              "flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border p-0",
              styled && "bg-primary text-white",
              !styled && "hover:bg-transparent",
            )}
          >
            <Ellipsis size={20} />
          </MenubarTrigger>
          <MenubarContent className='mx-2 gap-2 bg-background'>
            {project.deleted &&
            (isOrganizationOwner ||
              isAdmin ||
              isProjectOwner ||
              project.deleted_by === authConfig?.id) ? (
              <MenubarItem
                onClick={(e) => e.preventDefault()}
                className='flex items-center p-1 text-xs'
              >
                <RestoreProject
                  project={project}
                  className='flex w-full flex-row items-center justify-between'
                >
                  <div className='flex w-full items-center'>
                    <span className='min-w-[180px] flex-1 text-left'>
                      {translate("Restore Project")}
                    </span>
                  </div>
                </RestoreProject>
                <MenubarShortcut></MenubarShortcut>
              </MenubarItem>
            ) : !project.deleted ? (
              <>
                <MenubarItem
                  onClick={(e) => {
                    e.preventDefault()
                  }}
                  className='flex items-center p-1 text-xs'
                >
                  <PinProject project={project}>
                    <div className='flex w-full items-center'>
                      <span className='min-w-[180px] flex-1 text-left'>
                        {translate(pinnedByUser ? "Unpin" : "Pin")}
                      </span>
                      <Pin size={14} />
                    </div>
                  </PinProject>

                  <MenubarShortcut></MenubarShortcut>
                </MenubarItem>
                <Separator />
                <MenubarItem
                  onClick={(e) => {
                    e.preventDefault()
                  }}
                  className='flex items-center p-1 text-xs'
                >
                  <CloneProject project={project}>
                    <div className='flex w-full items-center'>
                      <span className='min-w-[180px] flex-1 text-left'>{translate("Clone")}</span>
                      <Copy size={14} />
                    </div>
                  </CloneProject>

                  <MenubarShortcut></MenubarShortcut>
                </MenubarItem>
                <Separator />
                <MenubarItem
                  onClick={() => setReminderOpen(true)}
                  className='flex items-center p-1 text-xs'
                >
                  <div className='flex w-full items-center'>
                    <span className='min-w-[180px] flex-1 text-left'>{translate("Set Reminder")}</span>
                    <Bell size={14} />
                  </div>
                </MenubarItem>
                <Separator />
                <MenubarItem
                  onClick={(e) => e.preventDefault()}
                  className='flex items-center p-1 text-xs text-red-500'
                >
                  <DeleteProject
                    project={project}
                    className='flex w-full flex-row items-center justify-between'
                  >
                    <div className='flex w-full items-center'>
                      <span className='min-w-[180px] flex-1 text-left'>{translate("Delete")}</span>
                      <Trash2 size={14} />
                    </div>
                  </DeleteProject>
                  <MenubarShortcut></MenubarShortcut>
                </MenubarItem>

                <Separator />
              </>
            ) : null}
          </MenubarContent>
        </MenubarMenu>
      </Menubar>
      {project?.id && (
        <ReminderSettingsModal
          projectId={project.id}
          open={reminderOpen}
          onOpenChange={setReminderOpen}
        />
      )}
    </div>
  )
}

export default ProjectDetailMenu
